/**
 * Responsible for performing form validation via the Constraint Validation API.
 * It provides both custom data attributes as well leverage any fields in a
 * given area (form or any container with elements) that are seen as invalid.
 * @module elements/fields/validator
 */
import $ from 'jquery';
import kebabCase from 'lodash/kebabCase';
import partial from 'lodash/partial';
import isEmail from 'validator/lib/isEmail';
import formScroller from './form-error-scroller';
import registerJQueryPlugin from '../../lib/register-jquery-plugin';

const PLUGIN_NAME = 'validator';
const { placeholder } = partial;

/**
 * Defines default options for a validator.
 * @type {Object}
 */
const VALIDATOR_OPTIONS = {
  /**
   * Allows for a form to be validated only upon submission.
   */
  validateOnlyOnSubmit: false,
  /**
   * Called when a submission is valid
   * @param {Array} validElements - An array of valid HTML elements
   */
  successCallback: validElements => validElements,
  /**
   * Called when a submission was unsuccessful
   * @param {Array} invalidElements - An array of invalid HTML elements
   */
  errorCallback: invalidElements => invalidElements,
  /**
   * Provides baseline implementation of where/how the page should scroll to
   * when an error has been found.
   * @param {number} x - The x coordinate to scroll to
   * @param {number} y - The y coordinate to scroll to
   */
  scrollTrigger: (x, y) => window.scrollTo(x, y),
  /**
   * Allows for custom scrolling when an error occurs.
   */
  useCustomFormErrorScroll: formScroller,
};

/**
 * Defines all validator related events.
 * @type {Object}
 */
export const EVENTS = {
  INVALID_SCROLL_POSITION: 'validator:invalid-scroll-position',
  VALIDATOR_SUBMIT: 'validator:submit',
  VALIDATOR_SUCCESS: 'validator:success',
  VALIDATOR_ERROR: 'validator:error',
  VALIDATOR_POST_SUCCESS: 'validator:post-success',
  VALIDATOR_POST_ERROR: 'validator:post-error',
};

/**
 * Defines the data attribute for triggering an element.
 * @type {string}
 */
export const VALIDATE_TRIGGER = 'data-validate-trigger';

/**
 * Defines default value for when a field is invalid
 * @type string
 */
export const DEFAULT_MESSAGE = 'Valid entry required';

/**
 * Defines a number of custom data attributes used for validating.
 * @private
 * @type {Object}
 */
const validators = {
  /**
   * Ensures that an element is not empty. This is different from a required
   * attribute as the validation occurs on submit by the validator verses
   * natively in the browser.
   *
   * @example
   * <form>
   *   <input type="text" data-validate-not-empty="true" />
   *   <button>Submit</button>
   * </form>
   *
   * @param {jQuery} $el - Elements checked to be not empty
   * @returns {string} Value used for setting custom validity
   */
  validateNotEmpty($el) {
    if (!$el.val()) {
      return $el.data('errorMessage') || DEFAULT_MESSAGE;
    }

    return '';
  },

  /**
   * Ensure that an element value is not set to the provided value. When it has
   * been set to the value provided, the element will be set as invalid.
   *
   * @example
   * <form>
   *   <input type="text" data-validate-not="walter" />
   *   <button>Submit</button>
   * </form>
   *
   * @param {jQuery} $el - Elements checked to be not empty
   * @param {string} dataVal - Elements checked to be not empty
   * @returns {string} Value used for setting custom validity
   */
  validateNot($el, dataVal) {
    if ($el.val() === dataVal) {
      return $el.data('errorMessage') || DEFAULT_MESSAGE;
    }

    return '';
  },

  /**
   * Ensure that a valid email has been provided. The field must have a handle
   * plus domain plus some kind of tld.
   * @param {jQuery} $el - Elements checked to be a valid email
   * @returns {string} Value used for setting custom validity
   */
  validateEmail($el) {
    const val = $el.val();
    // Make sure we have a value just in case the field is not required. That
    // can be handled by another data-validate-*.
    if (!val) {
      return '';
    }

    if (!isEmail(val)) {
      return $el.data('errorMessage') || DEFAULT_MESSAGE;
    }

    return '';
  },

  /**
   * Match a particular pattern
   * @param {jQuery} $el - Element to check
   * @returns {string} Value used for setting custom validity
   */
  validatePattern($el) {
    const val = $el.val();
    const regex = new RegExp($el.data('validate-pattern'));
    if (regex instanceof RegExp && !regex.test(val)) {
      return 'Does not match pattern';
    }

    return '';
  },
};

/**
 * Simple helper that will determine the top position of an element on the page.
 * @private
 * @param {HTMLElement} el - Element under question to find top position
 * @returns {number} The total distance from the top for an element
 */
const getTop = (el) => {
  const bodyTop = document.body.getBoundingClientRect().top;
  const elTop = el.getBoundingClientRect().top;
  return elTop - bodyTop;
};

/**
 * Figures out the highest elements found in the DOM based on an array of HTML
 * elements.
 * @private
 * @param {Array} invalidElements - A list of HTML elements
 * @returns {HTMLElement} Highest HTML element on the page
 */
const findHighestEl = (invalidElements) => {
  const [initial] = invalidElements;
  return invalidElements.reduce((prev, curr) =>
    (getTop(curr) < getTop(prev) ? curr : prev),
    initial,
  );
};

/**
 * Determines where in the form to scroll to when an error has been found, so
 * that the user has some context about what in the form has been filled in
 * incorrectly.
 * @private
 * @param {Array} invalidElements - A list of invalid elements
 * @param {Function} scrollTrigger - Called on to determine where to scroll to.
 */
const setInvalidScrollPosition = (invalidElements, scrollTrigger) => {
  const highestEl = findHighestEl(invalidElements);
  let yAxis = getTop(highestEl);
  let isDelegatedScroll = false;
  const dict = {
    detail: {
      highestEl,
      setElement: (el) => {
        yAxis = getTop(el);
      },
      setPosition: (pos) => {
        yAxis = window.scrollY + pos;
      },
      setRawPosition: (pos) => {
        yAxis = pos;
      },
      delegateScroll: (fn) => {
        fn(highestEl);
        isDelegatedScroll = true;
      },
    },
  };

  const evt = new CustomEvent(EVENTS.INVALID_SCROLL_POSITION, dict);
  highestEl.dispatchEvent(evt);

  if (!isDelegatedScroll) {
    scrollTrigger(0, yAxis);
  }
};

/**
 * Validates for form field based on whether it contains a custom data
 * attribute as specified by any validators. It's purpose is to set the proper
 * validity if the field's requirements haven't been met. It will return true
 * if the element was found invalid.
 * @private
 * @see validators
 * @param {HTMLSelectElement|HTMLInputElement} el - Form element
 * @returns {boolean|undefined} Determines if element is invalid
 */
const validateField = (el) => {
  const $el = $(el);

  if ($el.is(':hidden')) {
    el.setCustomValidity('');
    return false;
  }

  const dataAttrs = $el.data();
  const validatorList = Object.keys(validators);

  for (let i = 0, len = validatorList.length; i < len; i += 1) {
    const name = validatorList[i];
    const dataVal = dataAttrs[name];

    if (!!dataVal && !!validators[name]) {
      const message = validators[name]($el, dataVal, dataAttrs);
      if (message) {
        el.setCustomValidity(message);
        return true;
      }

      el.setCustomValidity('');
    }
  }

  return false;
};

/**
 * Helper that determines all the elements that contain data validator
 * attributes and applies a function to interact with each element.
 * @private
 * @param {jQuery} $wrapper - Container for the form elements
 * @param {Function} fn - Called and passed along a form element
 */
const forEachFormField = ($wrapper, fn) => {
  const dataSelector = Object
    .keys(validators)
    .map(name => `[data-${kebabCase(name)}]`)
    .join(',');

  $wrapper.find(dataSelector).each(function iterateElement() {
    fn(this);
  });
};

/**
 * Responsible for setting a custom error message if it has been provided
 * as a data attribute.
 * @param {Array} invalidElements - Elements considered invalid
 */
/**
 * Responsible for queries invalid elements in a container.
 * @param {jQuery} $wrapper - Container with form elements
 * @returns {Array} List of invalid elements
 */
const getInvalidElementList = $wrapper =>
  $wrapper
    .find(':invalid')
    .not(':hidden')
    .toArray();

/**
 * Responsible for queries valid elements in a container.
 * @param {jQuery} $wrapper - Container with form elements
 * @returns {Array} List of valid elements
 */
const getValidElementList = $wrapper =>
  $wrapper
    .find(':valid')
    .not('button,input[type="submit"]')
    .toArray();

/**
 * Responsible for verifying a set of elements.
 * @param {Array} elements - List of elements
 */
const verifyElementsValidity = (elements) => {
  elements.forEach((el) => {
    el.checkValidity();
  });
};

/**
 * Responsible for triggering a native CustomEvent so that native event
 * listeners ((add|remove)EventListener) and external listeners (such as jQuery)
 * can work in unison.
 * @param {jQuery} $el - Wrapper element
 * @param {string} evtName - Name of the event
 * @param {Object} detail - Custom data for the event
 */
const triggerEvent = ($el, evtName, detail) => {
  const errorEvt = new CustomEvent(evtName, { detail });
  $el[0].dispatchEvent(errorEvt);
};

/**
 * Responsible for taking a list of form elements and creating an object that
 * is keyed by the form element name and with a value of the form element.
 * @param {Array} elements - List of form elements
 * @returns {Object} Keyed form element name by value
 */
const createValuesMap = elements =>
  elements.reduce((acc, el) => {
    const $el = $(el);
    const name = $el.attr('name') || $el.attr('id');
    return $.extend({}, acc, {
      [name]: {
        $el,
        el: $el[0],
        value: $el.val(),
      },
    });
  }, {});

/**
 * Responsible for detecting whether the submission failed or was successful
 * and run through the proper processing/notifying.
 * @param {jQuery} $wrapper - Wrapper element
 * @param {Function} successCallback - Fired off if submission was successful
 * @param {Function} errorCallback - Fired off if submissions was unsuccessful
 * @param {Function} useCustomFormErrorScroll - Called on when an error has
 * been found
 * @param {Function} scrollTrigger - Called on to go a position on the page
 * @param {boolean} disableErrorScroll - Disables a form error from scrolling
 * to the error on the screen.
 * @returns {Function} Called on when the submission should be finalized
 */
const processSubmission = (
  $wrapper,
  successCallback,
  errorCallback,
  useCustomFormErrorScroll,
  scrollTrigger,
  disableErrorScroll,
) => (detail, { success, error }) => {
  const invalidElements = getInvalidElementList($wrapper);
  const validElements = getValidElementList($wrapper);
  const invalidValues = createValuesMap(invalidElements);
  const validValues = createValuesMap(validElements);
  const updatedDetail = $.extend({}, detail, {
    invalidElements,
    invalidValues,
    validElements,
    validValues,
  });
  verifyElementsValidity(invalidElements);

  if (!invalidElements.length) {
    successCallback(validElements);
    triggerEvent($wrapper, success, updatedDetail);
    return;
  }

  const shouldDisableErrorScroll = () =>
    !disableErrorScroll && !$wrapper.data('validateDisableErrorScroll');

  if (!useCustomFormErrorScroll($wrapper, scrollTrigger) && shouldDisableErrorScroll()) {
    setInvalidScrollPosition(invalidElements, scrollTrigger);
  }

  detail.srcEvent.preventDefault();
  errorCallback(invalidElements);
  triggerEvent($wrapper, error, updatedDetail);
};

/**
 * Performs the submit handler used for verifying the fields within an element.
 * @private
 * @param {Event} evt - DOM event
 * @param {jQuery} $wrapper - The element containing form elements
 * @param {Function} successCallback - Fired off if submission was successful
 * @param {Function} errorCallback - Fired off if submissions was unsuccessful
 * @param {Function} useCustomFormErrorScroll - Called on when an error has
 * been found
 * @param {Function} scrollTrigger - Called on to go a position on the page
 * @param {boolean} disableErrorScroll - Disables a form error from scrolling
 * to the error on the screen.
 */
const submitHandler = (
    evt,
    $wrapper,
    successCallback,
    errorCallback,
    useCustomFormErrorScroll,
    scrollTrigger,
    disableErrorScroll,
) => {
  forEachFormField($wrapper, validateField);

  const invalidElements = getInvalidElementList($wrapper);
  const validElements = getValidElementList($wrapper);

  verifyElementsValidity(invalidElements);

  let detail = {
    srcEvent: evt,
    validElements,
    validValues: createValuesMap(validElements),
    invalidElements,
    invalidValues: createValuesMap(invalidElements),
    pause() {
      evt.preventDefault();
    },
  };

  const processSubmissionFunc = processSubmission(
    $wrapper,
    successCallback,
    errorCallback,
    useCustomFormErrorScroll,
    scrollTrigger,
    disableErrorScroll,
  );

  detail = $.extend({}, detail, {
    revalidate() {
      processSubmissionFunc(detail, {
        success: EVENTS.VALIDATOR_POST_SUCCESS,
        error: EVENTS.VALIDATOR_POST_ERROR,
      });
    },
    proceed() {
      processSubmissionFunc(detail, {
        success: EVENTS.VALIDATOR_POST_SUCCESS,
        error: EVENTS.VALIDATOR_POST_ERROR,
      });

      const updatedInvalidElements = getInvalidElementList($wrapper);
      if (!updatedInvalidElements.length) {
        $wrapper[0].submit();
      }
    },
  });

  processSubmissionFunc(detail, {
    success: EVENTS.VALIDATOR_SUCCESS,
    error: EVENTS.VALIDATOR_ERROR,
  });

  triggerEvent($wrapper, EVENTS.VALIDATOR_SUBMIT, $.extend({}, detail, {
    hasErrors: !!invalidElements.length,
  }));
};

/**
 * Responsible for allowing a single handler to be called on to avoid a handler
 * to be called on multiple times.
 * @param {Function} handler - Func to call on
 * @returns {Function} An event callback
 */
const makeSingleEventCall = (handler) => {
  let called;
  return (evt) => {
    if (!called) {
      called = evt.type;
    }

    if (evt.type === called) {
      handler(evt);
    }
  };
};

/**
 * Determines the type of event used for refreshing. In other words, when a
 * field should be checked for validity.
 * @private
 * @param {HTMLInputElement|HTMLSelectElement} el - An element
 * @returns {string} The type of event used for determining a refresh of a field
 */
const resolveRefreshEventType = (el) => {
  switch (el.tagName.toLowerCase()) {
    case 'select':
      return 'change';
    default:
      return 'blur';
  }
};

/**
 * Based on the type of element, will apply an event listener whenever a value
 * has been finalized on the form element.
 * @private
 * @param {jQuery} $el - The form or container for form fields
 */
const listenForFieldChange = ($el) => {
  forEachFormField($el, (formField) => {
    const evtName = resolveRefreshEventType(formField);
    const callback = () => validateField(formField);
    formField.addEventListener(evtName, callback);
    formField.addEventListener('validate', callback);
  });
};

/**
 * Validates fields within an element, most likely a form based on what is
 * passed in. The validation of fields is done by a couple of ways:
 *
 * 1. By using browser defaults around validity. For example, if a form element
 * is using a required attribute, has a type that uses built in validation, has
 * a patter attribute, or is controlling validity programmatically by
 * JavaScript.
 * 2. By leveraging data attributes that are coming from within. Think of these
 * attributes as additional configuration that extends the capabilities of
 * validation that isn't supported by the browser.
 * 3. A combination of two.
 *
 * Whether either of these conditions are met, they are finally triggered by
 * either a form submission or a click event applied to an element that
 * contains a data-validate-trigger attribute. This would allow for form
 * submissions to be applied to an element other than a input[type="submit"] or
 * button, in case it's needed. When any form elements are found invalid, a
 * pseudo class of invalid is set and can be styled by CSS.
 *
 * The goal of this work is to leverage as much from within the browser and
 * remove the need for as much custom work as possible.
 * @see validators
 * @see VALIDATOR_OPTIONS
 * @param {HTMLElement} el - An element containing fields used for validating
 * @param {Object} [options={}] - Options used for overriding
 */
export default function validator(el, options = {}) {
  const $el = $(el);
  const $triggers = $el.find(`[${VALIDATE_TRIGGER}]`);
  const {
    validateOnlyOnSubmit,
    successCallback,
    errorCallback,
    scrollTrigger,
    useCustomFormErrorScroll,
    validateDisableErrorScroll: disableErrorScroll,
  } = $.extend({}, VALIDATOR_OPTIONS, options, $el.data());

  const partialSubmitHandler = partial(submitHandler,
    placeholder,
    $el,
    successCallback,
    errorCallback,
    useCustomFormErrorScroll,
    scrollTrigger,
    disableErrorScroll,
  );

  const submitHandlerFunc = makeSingleEventCall(partialSubmitHandler);

  $el.on('submit', submitHandlerFunc);

  if ($triggers.length) {
    $triggers.on('click', submitHandlerFunc);
  }

  if (!validateOnlyOnSubmit) {
    listenForFieldChange($el);
  }
}

registerJQueryPlugin(PLUGIN_NAME, validator);



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/validator.js